import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DataService } from '@Core/services/proprietary/data.service';
import { AppService } from '../../../../../core/services/proprietary/app.service';
import { InlineSearchService } from '../../../../../core/services/proprietary/inline-search.service';
import { SearchService } from '../../../../../core/services/proprietary/search.service';
import { SearchActionType, SearchOptionsType, SearchType } from '../../../../../core/services/proprietary/search.types';

const noop = () => { };

@Component({
    standalone: false,
    selector: 'ep-forced-search',
    template: `
    <div [class]="'unity-form-group'" style="margin:0px; padding:0px">
      <label *ngIf="!noLabel"><ng-content></ng-content></label>
      <div class="input-group" style="position:relative; flex-wrap: nowrap;">
        <input
          #forcedSearchInput
          autocomplete="off"
          type="text"
          [disabled]="controlDisabled"
          readonly="true"
          class="input-search w-100"
          [(ngModel)]="modelValue"
          [placeholder]="placeholder"
        />
        <div class="input-group-append" style="min-height:100%">
          <button
            class="btn btn-search"
            type="button"
            style="cursor:pointer;color: var(--unity-semantic-color-foreground-neutral-tertiary); 
                border-color: var(--unity-semantic-color-border-neutral-standard-alpha-default)"
            tabindex="-1"
            (mousedown)="$event.preventDefault(); $event.stopPropagation()"
            (click)="searchIconClicked($event); app.stopNavigation($event)"
            [ngbTooltip]="searchModel.currentOptions.tooltip ? searchModel.currentOptions.tooltip : 'Search Dialog'"
            tooltipClass="tooltip"
          >
            <svg class="unity-icon unity-icon-lg unity-icon-search">
              <use xlink:href="#unity-icon-search"></use>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: ForcedSearchComponent, multi: true }]
})
export class ForcedSearchComponent implements ControlValueAccessor, OnInit, OnDestroy {
    isModalOpen: boolean = false;

    _objectType: SearchType;
    _options: any = {};
    _currentOptions: any;

    public searchModel = {
        currentOptions: <SearchOptionsType>{},
        criteria: <any>{},
        dirty: false,
        searchText: '',
        objectType: ''
    };

    private searchResultObjProps: any = {};

    _modelValue: string | null | undefined = '';

    @Input() set modelValue(value: string | null | undefined) {
        if (value !== this._modelValue) {
            this._modelValue = value;
        }
    }

    get modelValue() {
        return this._modelValue;
    }

    @ViewChild('forcedSearchInput', { read: ElementRef }) forcedSearchInput: ElementRef;

    @Input('disabled')
    controlDisabled: boolean = true;

    @Input()
    placeholder: string = '';

    @Input()
    noLabel: boolean = false;

    @Input()
    formControlPath: string = '';

    @Input()
    descFormControlPath: string = '';

    @Input()
    criteria: any = {};

    @Input() set options(value: any) {
        if (value) {
            this._options = { ...value };
        }
    }

    get options() {
        return this._options;
    }

    @Input()
    set objectType(value: string) {
        if (value) {
            this._objectType = <SearchType>value.toLowerCase();
            this.searchModel.objectType = value;
        }
    }

    @Output()
    blur = new EventEmitter();

    @Output()
    focus = new EventEmitter();

    @Output()
    searchValueChange = new EventEmitter();

    @Output()
    dataSelected = new EventEmitter();

    @Output()
    dataCleared = new EventEmitter();

    private onTouchedCallback: () => void = noop;
    private onChangeCallback: (_: any) => void = noop;

    constructor(
        public app: AppService,
        public ds: DataService,
        private cdRef: ChangeDetectorRef,
        private inlineSearch: InlineSearchService,
        private dialogSearch: SearchService
    ) { }

    ngOnInit() {
        if (this.searchModel.objectType != 'local') {
            this.searchModel.currentOptions = this.inlineSearch.getDefaultOptions(
                this._options,
                this._objectType,
                this.formControlPath,
                this.descFormControlPath
            );

            this.searchResultObjProps = this.inlineSearch.getForcedSearchResultProperties(this._objectType);
        }
    }

    ngOnDestroy() { }

    setSearchCriteria() {
        this.searchModel.criteria = this.inlineSearch.setInlineSearchCriteria(
            this.criteria,
            this.searchModel.currentOptions,
            this._objectType,
            this.searchModel.searchText
        );
    }

    searchIconClicked(event: any) {
        if (this.searchModel.objectType == 'local') {
            this.dataSelected.emit(true);
        } else {
            this.openSearchDialog();
        }
    }

    openSearchDialog(forceAutoRetrieve: boolean = false) {
        if (!this.isModalOpen) {
            this.isModalOpen = true;

            this.setSearchCriteria();

            let searchOptions = { ...this.searchModel.currentOptions };
            if (forceAutoRetrieve) {
                searchOptions.autoSearch = true;
            }

            this.dialogSearch.openDialog(
                this._objectType,
                this.modalResultUpdated.bind(this),
                this.searchModel.criteria,
                searchOptions
            );
        }
    }

    modalResultUpdated(result: any) {
        if (result && result.action) {
            switch (result.action) {
                case SearchActionType.Close:
                case SearchActionType.Cancel:
                    break;
                case SearchActionType.Clear:
                    this.clear();
                    break;
                case SearchActionType.Select:
                    this.selectResult(result.data);
                    break;
            }
        }
        this.isModalOpen = false;
    }

    selectResult(result: any) {
        let data = result;

        let modelVal = data[0][this.searchResultObjProps.modelProperty];
        let descVal = data[0][this.searchResultObjProps.descriptionProperty];

        let forcedSearch = {
            [this.searchModel.currentOptions.modelProperty]: modelVal,
            [this.searchModel.currentOptions.descriptionProperty]: descVal
        };

        this.onChange(forcedSearch);
        this.dataSelected.emit(result);
    }

    clear() {
        this.modelValue = '';
        this.searchValueChange.emit({ Value: '', Desc: '' });
        this.dataCleared.emit();
    }

    onChange(newValue: any) {
        this.modelValue = newValue[this.searchModel.currentOptions.modelProperty];
        this.searchValueChange.emit({
            Value: newValue[this.searchModel.currentOptions.modelProperty],
            Desc: newValue[this.searchModel.currentOptions.descriptionProperty]
        });
    }

    onBlur() {
        this.blur.emit();
        this.onTouchedCallback();
    }

    onFocus() {
        this.focus.emit();
    }

    writeValue(value: any): void {
        if (value !== this.modelValue) {
            this.modelValue = value;
            this.cdRef.detectChanges();
        }
    }

    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled = isDisabled;
    }
}
